/**
 * AccountSearchModal — search overlay for picking an account or party.
 * Party tab is a two-step flow: (1) search + pick party, (2) pick role account.
 */

import React from 'react'

import { cn } from '@elsapiens/utils'
import { createPortal } from 'react-dom'

import { type Party, PartyType } from '@/gen/proto'

import { handleKeyDown } from './AccountSearchModal-keyboard'
import { useAccountSearchModal } from './useAccountSearchModal'
import { groupAccounts } from '../../pages/journal-entry-form/journal-entry-form-helpers'
import { type Account } from '../../services/account-service'
import { type PartyRoleAccountItem } from '../../services/party-service'
import { useOptionalCurrentLedgerId } from '../../stores/ledger-store'
import { PartyDialog } from '../billing/PartyDialog'

export interface AccountSearchModalProps {
  isOpen: boolean
  onClose: () => void
  accounts: Account[]
  onSelect: (account: Account) => void
  /** When provided, the Parties tab appears. */
  onSelectParty?: (party: Party, role: string, purpose: string, accountId: string) => void
  initialSearch?: string
  baseCurrency?: string
  initialMode?: 'account' | 'party'
  organizationId?: string
  /** Pre-filter party search to a specific role: 'customer' or 'supplier'. */
  partyRoleFilter?: string
}

function partyTypeFromRoleFilter(roleFilter: string | undefined): PartyType {
  switch (roleFilter) {
    case 'customer': return PartyType.PARTY_TYPE_CUSTOMER
    case 'supplier': return PartyType.PARTY_TYPE_VENDOR
    default: return PartyType.PARTY_TYPE_BOTH
  }
}

// ── Header sub-components ──────────────────────────────────────────────────

interface TabsHeaderProps {
  activeMode: 'account' | 'party'
  setActiveMode: (m: 'account' | 'party') => void
  setSearch: (s: string) => void
  inputRef: React.RefObject<HTMLInputElement>
}

function TabsHeader({ activeMode, setActiveMode, setSearch, inputRef }: TabsHeaderProps): JSX.Element {
  const onTab = (mode: 'account' | 'party') => (): void => {
    setActiveMode(mode); setSearch(''); setTimeout(() => inputRef.current?.focus(), 30)
  }
  const tabClass = (mode: 'account' | 'party'): string => cn(
    'flex-1 px-4 py-2 text-sm font-medium transition-colors',
    activeMode === mode ? 'text-primary border-b-2 border-primary -mb-px bg-card' : 'text-foreground-secondary hover:text-foreground hover:bg-muted',
  )
  return (
    <div className="flex border-b border-border">
      <button className={tabClass('account')} type="button" onClick={onTab('account')}>Accounts</button>
      <button className={tabClass('party')} type="button" onClick={onTab('party')}>Parties</button>
    </div>
  )
}

function Step2Header({ selectedParty, onBack }: { selectedParty: Party; onBack: () => void }): JSX.Element {
  const step2Title = `${selectedParty.code} — ${selectedParty.name}`
  return (
    <div className="flex items-center gap-2 el-px-md py-2 border-b border-border bg-muted/50">
      <button className="p-1 rounded hover:bg-accent text-foreground-secondary hover:text-foreground transition-colors" title="Back to parties (← or Esc)" type="button" onClick={onBack}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} /></svg>
      </button>
      <span className="text-sm font-medium truncate" title={step2Title}>{step2Title}</span>
      <span className="ml-auto text-xs text-foreground-secondary">Select account type</span>
    </div>
  )
}

interface SearchInputProps {
  inputRef: React.RefObject<HTMLInputElement>
  search: string
  setSearch: (s: string) => void
  activeMode: 'account' | 'party'
}

function SearchInput({ inputRef, search, setSearch, activeMode }: SearchInputProps): JSX.Element {
  return (
    <div className="el-p-md border-b border-border">
      <div className="relative">
        <svg className="absolute top-1/2 w-5 h-5 text-foreground-secondary" fill="none" stroke="currentColor" style={{ left: 'var(--el-spacing-px-md)', transform: 'translateY(-50%)' }} viewBox="0 0 24 24">
          <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
        <input
          ref={inputRef}
          autoComplete="off"
          className="w-full text-base bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-foreground-secondary/60"
          placeholder={activeMode === 'party' ? 'Search parties by name or code…' : 'Search accounts by code or name…'}
          style={{ paddingInlineStart: 'calc(var(--el-spacing-px-md) + 1.25rem + var(--el-spacing-px-sm))', paddingInlineEnd: 'var(--el-spacing-px-md)', paddingBlock: 'var(--el-spacing-py-md)' }}
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
    </div>
  )
}

// ── Footer ─────────────────────────────────────────────────────────────────

function Footer({ selectedParty, shouldShowPartyTab, activeMode }: {
  selectedParty: Party | null; shouldShowPartyTab: boolean; activeMode: 'account' | 'party'
}): JSX.Element {
  return (
    <div className="el-px-xs el-py-xs border-t border-border text-xs text-foreground-secondary flex items-center el-gap-md">
      <span><kbd className="kbd">↑↓</kbd> navigate</span>
      <span><kbd className="kbd">↵</kbd> select</span>
      {selectedParty
        ? <span><kbd className="kbd">←</kbd> back</span>
        : <span><kbd className="kbd">esc</kbd> close</span>}
      {!selectedParty && shouldShowPartyTab && (
        <span><kbd className="kbd">⌘</kbd>+<kbd className="kbd">←/→</kbd> switch tab</span>
      )}
      {!selectedParty && shouldShowPartyTab && activeMode === 'party' && (
        <span className="ml-auto text-foreground-secondary/70">↵ to see accounts</span>
      )}
    </div>
  )
}

// ── List rendering dispatcher ─────────────────────────────────────────────

interface ListAreaProps {
  state: ReturnType<typeof useAccountSearchModal>
  baseCurrency?: string
  onSelect: (account: Account) => void
  onClose: () => void
}

function ListArea({ state, baseCurrency, onSelect, onClose }: ListAreaProps): JSX.Element {
  return (
    <div ref={state.listRef} className="max-h-80 overflow-y-auto">
      {state.activeMode === 'account' && (
        <AccountList
          baseCurrency={baseCurrency}
          filteredAccounts={state.filteredAccounts}
          flatAccounts={state.flatAccounts}
          groupedAccounts={state.groupedAccounts}
          search={state.search}
          selectedIndex={state.selectedIndex}
          onHover={state.setSelectedIndex}
          onSelect={account => { onSelect(account); onClose() }}
        />
      )}
      {state.activeMode !== 'account' && state.selectedParty && (
        <RoleAccountList
          creatingIndex={state.creatingIndex}
          error={state.roleLoadError}
          isLoading={state.isLoadingRoles}
          items={state.roleAccounts}
          selectedIndex={state.roleSelectedIndex}
          onHover={state.setRoleSelectedIndex}
          onSelect={(item) => { void state.selectRoleAccount(item, state.roleSelectedIndex) }}
        />
      )}
      {state.activeMode !== 'account' && !state.selectedParty && (
        <PartySearchList
          isLoading={state.isLoadingParties}
          parties={state.parties}
          search={state.search}
          selectedIndex={state.partySelectedIndex}
          onAddNew={() => state.setIsAddPartyOpen(true)}
          onHover={state.setPartySelectedIndex}
          onSelect={state.pickParty}
        />
      )}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────

export function AccountSearchModal(props: AccountSearchModalProps): JSX.Element | null {
  const { isOpen, onClose, accounts, onSelect, onSelectParty, initialSearch = '', baseCurrency,
    initialMode = 'account', organizationId = '', partyRoleFilter } = props
  const currentLedgerId = useOptionalCurrentLedgerId()
  const state = useAccountSearchModal({
    isOpen, onClose, accounts, initialSearch, initialMode, organizationId, partyRoleFilter, onSelectParty,
  })

  if (!isOpen) { return null }

  const shouldShowPartyTab = !!onSelectParty
  const onKeyDown = (e: React.KeyboardEvent): void => {
    handleKeyDown(e, { state, shouldShowPartyTab, onSelect, onClose })
  }

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- modal dialog needs keyboard navigation
    <div aria-modal="true" className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" role="dialog" tabIndex={-1} onKeyDown={onKeyDown}>
      <div aria-hidden="true" className="absolute inset-0 bg-black/40" role="presentation" onClick={onClose} />
      <div ref={state.containerRef} className="relative w-full max-w-lg bg-card rounded-xl shadow-2xl border border-border overflow-hidden" onBlur={state.handleBlur}>
        {shouldShowPartyTab && !state.selectedParty && (
          <TabsHeader activeMode={state.activeMode} inputRef={state.inputRef} setActiveMode={state.setActiveMode} setSearch={state.setSearch} />
        )}
        {state.selectedParty && (
          <Step2Header selectedParty={state.selectedParty} onBack={state.goBackToPartySearch} />
        )}
        {!state.selectedParty && (
          <SearchInput activeMode={state.activeMode} inputRef={state.inputRef} search={state.search} setSearch={state.setSearch} />
        )}
        {state.selectedParty && <input ref={state.inputRef} readOnly className="sr-only" tabIndex={-1} />}
        <ListArea baseCurrency={baseCurrency} state={state} onClose={onClose} onSelect={onSelect} />
        <Footer activeMode={state.activeMode} selectedParty={state.selectedParty} shouldShowPartyTab={shouldShowPartyTab} />
      </div>
      {state.isAddPartyOpen && (
        <PartyDialog
          isOpen={state.isAddPartyOpen}
          ledgerId={currentLedgerId ?? ''}
          orgId={organizationId}
          partyType={partyTypeFromRoleFilter(partyRoleFilter)}
          onClose={() => state.setIsAddPartyOpen(false)}
          onSuccess={(party) => {
            state.setIsAddPartyOpen(false)
            state.bumpPartyListVersion()
            state.setSelectedParty(party)
          }}
        />
      )}
    </div>,
    document.body,
  )
}

// ── AccountList ────────────────────────────────────────────────────────────────

interface AccountListProps {
  baseCurrency?: string
  filteredAccounts: Account[]
  flatAccounts: Account[]
  groupedAccounts: ReturnType<typeof groupAccounts>
  search: string
  selectedIndex: number
  onSelect: (account: Account) => void
  onHover: (index: number) => void
}

function AccountRowFlat({ account, isSelected, baseCurrency, onSelect, onHover, index }: {
  account: Account; isSelected: boolean; baseCurrency?: string; onSelect: () => void; onHover: () => void; index: number
}): JSX.Element {
  return (
    <button key={account.id} className={cn('w-full el-px-md py-1.5 text-left flex items-center el-gap-sm transition-colors', isSelected ? 'bg-accent' : 'hover:bg-muted')} data-account-id={account.id} data-index={index} type="button" onClick={onSelect} onMouseEnter={onHover}>
      <span className="font-mono text-sm text-muted-foreground whitespace-nowrap flex-shrink-0">{account.code}</span>
      <span className="flex-1 truncate">{account.name}</span>
      {account.currency && baseCurrency && account.currency !== baseCurrency && <span className="px-1.5 py-0 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded flex-shrink-0">{account.currency}</span>}
      {isSelected && <span className="text-xs text-foreground-secondary shrink-0">↵</span>}
    </button>
  )
}

function AccountList(props: AccountListProps): JSX.Element {
  const { baseCurrency, filteredAccounts, flatAccounts, groupedAccounts, search, selectedIndex, onSelect, onHover } = props
  if (flatAccounts.length === 0) { return <div className="el-p-lg text-center text-foreground-secondary">No accounts found</div> }

  if (search) {
    return (
      <>
        {filteredAccounts.map((account, index) => (
          <AccountRowFlat
            key={account.id}
            account={account}
            baseCurrency={baseCurrency}
            index={index}
            isSelected={index === selectedIndex}
            onHover={() => onHover(index)}
            onSelect={() => onSelect(account)}
          />
        ))}
      </>
    )
  }

  return (
    <>
      {groupedAccounts.map(group => (
        <div key={group.type}>
          <div className="el-px-sm py-1 text-[10px] font-bold text-foreground uppercase tracking-widest bg-muted border-y border-border sticky top-0">{group.label}</div>
          {group.accounts.map(account => {
            const index = flatAccounts.indexOf(account)
            return (
              <AccountRowFlat
                key={account.id}
                account={account}
                baseCurrency={baseCurrency}
                index={index}
                isSelected={index === selectedIndex}
                onHover={() => onHover(index)}
                onSelect={() => onSelect(account)}
              />
            )
          })}
        </div>
      ))}
    </>
  )
}

// ── PartySearchList (step 1) ───────────────────────────────────────────────────

interface PartySearchListProps {
  parties: Party[]
  isLoading: boolean
  search: string
  selectedIndex: number
  onSelect: (party: Party) => void
  onHover: (index: number) => void
  onAddNew: () => void
}

function PartySearchList({ parties, isLoading, search, selectedIndex, onSelect, onHover, onAddNew }: PartySearchListProps): JSX.Element {
  if (isLoading) { return <div className="el-p-lg text-center text-foreground-secondary text-sm">Searching…</div> }

  const addRowIndex = parties.length
  const isAddSelected = selectedIndex === addRowIndex

  return (
    <>
      {parties.length === 0 && (
        <div className="el-p-lg text-center text-foreground-secondary text-sm">
          {search ? 'No parties found' : 'Start typing to search parties'}
        </div>
      )}
      {parties.map((party, index) => {
        const isSelected = index === selectedIndex
        const roles = party.roles ?? []
        return (
          <button
            key={party.id}
            className={cn('w-full flex items-center gap-2 el-px-md py-2 border-b border-border last:border-0 text-left transition-colors', isSelected ? 'bg-accent' : 'hover:bg-muted')}
            data-party-index={index}
            type="button"
            onClick={() => onSelect(party)}
            onMouseEnter={() => onHover(index)}
          >
            <span className="font-mono text-sm text-muted-foreground whitespace-nowrap flex-shrink-0">{party.code}</span>
            <span className="flex-1 truncate text-sm">{party.name}</span>
            <span className="flex gap-1 flex-shrink-0">
              {roles.map(r => (
                <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-muted border border-border text-foreground-secondary font-medium uppercase">{r}</span>
              ))}
            </span>
            {isSelected && <span className="text-xs text-foreground-secondary shrink-0">↵</span>}
          </button>
        )
      })}
      <button
        className={cn('w-full flex items-center gap-2 el-px-md py-2 text-left transition-colors text-primary font-medium', isAddSelected ? 'bg-accent' : 'hover:bg-muted')}
        data-party-index={addRowIndex}
        type="button"
        // preventDefault on mousedown so the search input keeps focus —
        // otherwise the input's blur handler closes AccountSearchModal before
        // the click handler can open the PartyDialog (Safari/Firefox quirk:
        // buttons don't auto-focus on click).
        onClick={onAddNew}
        onMouseDown={e => e.preventDefault()}
        onMouseEnter={() => onHover(addRowIndex)}
      >
        <span className="text-base leading-none">+</span>
        <span className="flex-1 text-sm">New party{search ? ` "${search}"` : ''}</span>
        {isAddSelected && <span className="text-xs text-foreground-secondary shrink-0">↵</span>}
      </button>
    </>
  )
}

// ── RoleAccountList (step 2) ──────────────────────────────────────────────────

interface RoleAccountListProps {
  items: PartyRoleAccountItem[]
  isLoading: boolean
  error: string | null
  selectedIndex: number
  creatingIndex: number | null
  onSelect: (item: PartyRoleAccountItem, index: number) => void
  onHover: (index: number) => void
}

function RoleAccountInfo({ item, hasAccount, isCreating }: {
  item: PartyRoleAccountItem; hasAccount: boolean; isCreating: boolean
}): JSX.Element {
  if (hasAccount) {
    return (
      <>
        <span className="font-mono text-xs text-muted-foreground flex-shrink-0">{item.accountCode}</span>
        <span className="truncate text-sm">{item.accountName}</span>
      </>
    )
  }
  if (isCreating) { return <span className="text-xs text-foreground-secondary">Creating…</span> }
  return <span className="text-amber-500 text-sm" title="Will be auto-created on select">★ Auto-create</span>
}

function RoleAccountList({ items, isLoading, error, selectedIndex, creatingIndex, onSelect, onHover }: RoleAccountListProps): JSX.Element {
  if (isLoading) { return <div className="el-p-lg text-center text-foreground-secondary text-sm">Loading accounts…</div> }
  if (error) { return <div className="el-p-lg text-center text-red-500 text-sm">{error}</div> }
  if (items.length === 0) { return <div className="el-p-lg text-center text-foreground-secondary text-sm">No role accounts configured for this party</div> }

  return (
    <>
      {items.map((item, index) => {
        const isSelected = index === selectedIndex
        const isCreating = creatingIndex === index
        const hasAccount = !!item.accountId
        return (
          <button
            key={`${item.role}|${item.purpose}`}
            className={cn('w-full flex items-center gap-3 el-px-sm py-2 border-b border-border last:border-0 text-left transition-colors', isSelected ? 'bg-accent' : 'hover:bg-muted', isCreating && 'opacity-60 cursor-wait')}
            data-role-index={index}
            disabled={isCreating}
            type="button"
            onClick={() => onSelect(item, index)}
            onMouseEnter={() => onHover(index)}
          >
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium uppercase flex-shrink-0">{item.roleLabel}</span>
            <span className="text-sm flex-shrink-0 text-foreground-secondary">{item.purposeLabel}</span>
            <span className="flex-1 min-w-0 flex items-center gap-1.5 overflow-hidden">
              <RoleAccountInfo hasAccount={hasAccount} isCreating={isCreating} item={item} />
            </span>
            {isSelected && !isCreating && <span className="text-xs text-foreground-secondary shrink-0">↵</span>}
          </button>
        )
      })}
    </>
  )
}
